import { useState, useEffect } from "react";
import {
  Mail, Loader2, Lock, Facebook, Table2, MessageCircle,
  RefreshCw, Search, XCircle, Clock, Activity,
  ChevronDown, ChevronUp, Zap, AlertTriangle, ArrowLeft, ArrowUpRight,
  Link2, AudioLines, Settings2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, formatDistanceToNowStrict } from "date-fns";
import { useBusiness } from "@/contexts/BusinessContext";

import EmailIntegration from "./EmailIntegration";
import FacebookIntegration from "./FacebookIntegration";
import GoogleSheetsIntegration from "./GoogleSheetsIntegration";
import WhatsAppIntegration from "./WhatsAppIntegration";
import WhatsAppConnect from "@/pages/whatsapp/WhatsAppConnect";
import WebhookIntegration from "./WebhookIntegration";
import ElevenLabsIntegration from "./ElevenLabsIntegration";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Presentation helpers ────────────────────────────────────────────────

// Epoch-0 / invalid timestamps come out of `Math.max(...new Date(x || 0))`
// when nothing has ever synced — treat them as "never", not 01/01/1970.
function toDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) || d.getTime() <= 0 ? null : d;
}
function timeAgo(value) {
  const d = toDate(value);
  return d ? formatDistanceToNowStrict(d, { addSuffix: true }) : "Never";
}
function exactTime(value) {
  const d = toDate(value);
  return d ? format(d, "d MMM yyyy, h:mm a") : "";
}
function latestOf(items, key) {
  const ts = items.map((i) => toDate(i[key])).filter(Boolean).map((d) => d.getTime());
  return ts.length ? new Date(Math.max(...ts)) : null;
}
const PERIOD_LABEL = { today: "today", "7days": "last 7 days", "30days": "last 30 days", all: "all time" };

const STATUS_META = {
  connected:    { label: "Connected",       pill: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500", live: true },
  error:        { label: "Needs attention", pill: "bg-amber-50 text-amber-700 ring-amber-200",       dot: "bg-amber-500" },
  syncing:      { label: "Syncing…",        pill: "bg-sky-50 text-sky-700 ring-sky-200",             dot: "bg-sky-500" },
  disconnected: { label: "Not connected",   pill: "bg-slate-100 text-slate-600 ring-slate-200",      dot: "bg-slate-400" },
};

function StatusPill({ status, name }) {
  const meta = STATUS_META[status] || STATUS_META.disconnected;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${meta.pill}`}
      data-testid={`status-badge-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="relative flex h-1.5 w-1.5">
        {meta.live && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${meta.dot}`} />}
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      </span>
      {meta.label}
    </span>
  );
}

function IconTile({ icon: Icon, accent, size = "md" }) {
  const box = size === "sm" ? "h-8 w-8 rounded-lg" : size === "lg" ? "h-12 w-12 rounded-2xl" : "h-11 w-11 rounded-xl";
  const glyph = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : "h-5 w-5";
  return (
    <span className={`flex shrink-0 items-center justify-center text-white shadow-sm ${box}`} style={{ backgroundColor: accent }}>
      <Icon className={glyph} />
    </span>
  );
}

// ── Integration card ────────────────────────────────────────────────────

function IntegrationCard({
  icon, accent, name, description, status, lastSync, autoSync, extraInfo,
  isActive, onClick, onConnect, onSyncNow, onConfigure,
}) {
  const connected = status === "connected";
  const info = (extraInfo || []).filter(Boolean);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      data-testid={`integration-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`group relative flex h-full flex-col rounded-2xl border bg-white p-5 text-left outline-none transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)] focus-visible:ring-2 focus-visible:ring-indigo-300 ${
        isActive ? "border-indigo-300 ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      {connected && <span className="absolute inset-x-5 top-0 h-[3px] rounded-b-full" style={{ backgroundColor: accent }} />}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <IconTile icon={icon} accent={accent} />
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold leading-tight text-slate-900">{name}</h3>
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{description}</p>
          </div>
        </div>
        <StatusPill status={status} name={name} />
      </div>

      {connected && (
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-xl bg-slate-50 px-3.5 py-3 text-xs">
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Last sync</dt>
            <dd className="mt-0.5 font-medium text-slate-800" title={exactTime(lastSync)}>{timeAgo(lastSync)}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Auto-sync</dt>
            <dd className={`mt-0.5 inline-flex items-center gap-1.5 font-medium ${autoSync ? "text-emerald-700" : "text-slate-500"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${autoSync ? "bg-emerald-500" : "bg-slate-300"}`} />
              {autoSync ? "On" : "Off"}
            </dd>
          </div>
          {info.slice(0, 2).map((line, i) => (
            <div key={i} className="col-span-2 flex items-center gap-2 truncate border-t border-slate-200/70 pt-2 text-slate-600 first:border-t-0">
              <span className="h-1 w-1 shrink-0 rounded-full bg-slate-300" />
              <span className="truncate">{line}</span>
            </div>
          ))}
        </dl>
      )}

      {status === "error" && info[0] && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{info[0]}</span>
        </div>
      )}

      <div className="mt-auto pt-5">
        {connected ? (
          <div className="flex gap-2">
            <Button
              size="sm" variant="outline"
              className="h-9 flex-1 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={(e) => { e.stopPropagation(); onConfigure?.(); }}
            >
              <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Configure
            </Button>
            <Button
              size="sm"
              className="h-9 flex-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={(e) => { e.stopPropagation(); onSyncNow?.(); }}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync now
            </Button>
          </div>
        ) : (
          <Button
            size="sm" variant="outline"
            className="h-9 w-full rounded-lg border-indigo-200 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50"
            onClick={(e) => { e.stopPropagation(); onConnect?.(); }}
          >
            {status === "error" ? "Finish setup" : "Connect"}
            <ArrowUpRight className="ml-1.5 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Sync activity ───────────────────────────────────────────────────────

const LOG_ICON = { email: Mail, facebook: Facebook, sheets: Table2, whatsapp: MessageCircle, webhook: Link2, elevenlabs: AudioLines };
const LOG_ACCENT = { email: "#2563eb", facebook: "#1877f2", sheets: "#16a34a", whatsapp: "#22c55e", webhook: "#7c3aed", elevenlabs: "#0f172a" };

function SyncActivityPanel({ logs, dateFilter, onDateFilterChange }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex cursor-pointer items-center justify-between gap-4 px-5 py-4 sm:px-6" onClick={() => setExpanded((v) => !v)}>
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
            <Activity className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900">Sync activity</h2>
            <p className="text-xs text-slate-500">Recent synchronization history across all sources</p>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Select value={dateFilter} onValueChange={onDateFilterChange}>
            <SelectTrigger className="h-8 w-36 rounded-lg text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <button type="button" onClick={() => setExpanded((v) => !v)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        logs.length === 0 ? (
          <div className="border-t border-slate-100 px-6 py-12 text-center">
            <Clock className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-700">No sync activity yet</p>
            <p className="mt-1 text-xs text-slate-500">Logs will appear here after your first sync.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border-t border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-6 py-3 font-semibold">Source</th>
                  <th className="px-3 py-3 font-semibold">When</th>
                  <th className="px-3 py-3 font-semibold">Leads</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-6 py-3 font-semibold">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log, i) => {
                  const Icon = LOG_ICON[log.integration_type] || Link2;
                  const status = log.status === "success" ? "connected" : log.status === "partial" ? "error" : "disconnected";
                  return (
                    <tr key={i} className="hover:bg-slate-50/70">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2.5">
                          <IconTile icon={Icon} accent={LOG_ACCENT[log.integration_type] || "#64748b"} size="sm" />
                          <span className="font-medium text-slate-800">{log.integration_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        <span title={exactTime(log.created_at)}>{timeAgo(log.created_at)}</span>
                      </td>
                      <td className="px-3 py-3 font-semibold text-emerald-600">+{log.leads_imported || 0}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
                          log.status === "success" ? STATUS_META.connected.pill : log.status === "partial" ? STATUS_META.error.pill : "bg-rose-50 text-rose-700 ring-rose-200"
                        }`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="max-w-xs truncate px-6 py-3 text-slate-500">{log.message || log.error || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────

export default function Integrations() {
  const { businessId } = useBusiness();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeIntegration, setActiveIntegration] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("7days");
  const [syncLogs, setSyncLogs] = useState([]);

  // Integration States
  const [integrations, setIntegrations] = useState([]);
  const [fbConfig, setFbConfig] = useState({ configured: false, app_id: null });
  const [fbIntegrations, setFbIntegrations] = useState([]);
  const [sheetIntegrations, setSheetIntegrations] = useState([]);
  const [waIntegrations, setWaIntegrations] = useState([]);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [elSettings, setElSettings] = useState(null);

  const fetchElSettings = async () => {
    try {
      const res = await fetch(BACKEND_URL + "/api/ai-conversations/settings" + (businessId ? "?business_id=" + businessId : ""), { credentials: "include" });
      if (res.ok) setElSettings(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchData();
    fetchFacebookConfig();
    fetchSyncLogs();
    fetchElSettings();
  }, [businessId]);

  const fetchData = async () => {
    try {
      const userRes = await fetch(BACKEND_URL + "/api/auth/me", { credentials: "include" });
      if (userRes.ok) setCurrentUser(await userRes.json());

      const intRes = await fetch(BACKEND_URL + "/api/email/integrations" + (businessId ? "?business_id=" + businessId : ""), { credentials: "include" });
      if (intRes.ok) setIntegrations((await intRes.json()).integrations || []);

      await fetchFacebookIntegrations();
      await fetchSheetIntegrations();
      await fetchWhatsAppIntegrations();
      await fetchWebhookConfig();
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchFacebookConfig = async () => {
    try {
      const res = await fetch(BACKEND_URL + "/api/facebook/config", { credentials: "include" });
      if (res.ok) setFbConfig(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchFacebookIntegrations = async () => {
    try {
      // /api/integrations/facebook/setup returns the current per-business config
      // (or a default empty state). Transform to the array shape the card expects
      // so the existing "connected count / last sync / forms_count" logic works.
      const res = await fetch(BACKEND_URL + "/api/integrations/facebook/setup" + (businessId ? "?business_id=" + businessId : ""), { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        const connected = !!(d.page_id && d.page_access_token_present);
        setFbIntegrations(connected ? [{
          page_id: d.page_id,
          page_name: d.page_name || d.page_id,
          enabled: !!d.enabled,
          forms_count: Array.isArray(d.form_ids) ? d.form_ids.length : 0,
          last_sync: d.updated_at || null,
        }] : []);
      } else {
        setFbIntegrations([]);
      }
    } catch (e) { console.error(e); setFbIntegrations([]); }
  };

  const fetchSheetIntegrations = async () => {
    try {
      const res = await fetch(BACKEND_URL + "/api/integrations/simple-sheets" + (businessId ? "?business_id=" + businessId : ""), { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSheetIntegrations(Array.isArray(data) ? data : data.integrations || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchWhatsAppIntegrations = async () => {
    try {
      const res = await fetch(BACKEND_URL + "/api/whatsapp/integrations" + (businessId ? "?business_id=" + businessId : ""), { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setWaIntegrations(data.integrations || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchWebhookConfig = async () => {
    try {
      const res = await fetch(BACKEND_URL + "/api/integrations/webhook", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const configs = data.configs || [];
        setWebhookConfig(configs.length > 0 ? configs[0] : null);
      }
    } catch (e) { console.error(e); }
  };

  const fetchSyncLogs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/sync-logs?filter=${dateFilter}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSyncLogs(data.logs || []);
      }
    } catch (e) { console.error(e); }
  };

  const handleRefresh = () => {
    fetchData();
    fetchSyncLogs();
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    const loadingToast = toast.loading("Syncing all integrations...");
    try {
      const params = businessId ? `?business_id=${businessId}` : "";
      const res = await fetch(`${BACKEND_URL}/api/integrations/sync-all${params}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      toast.dismiss(loadingToast);
      if (res.ok) {
        const data = await res.json();
        const newLeads = data.total_new_leads || 0;
        const sources = data.sources_synced || 0;
        const errs = (data.errors || []).length;
        if (data.status === "success" || errs === 0) {
          toast.success(`✅ ${newLeads} new leads synced from ${sources} source${sources !== 1 ? "s" : ""}`);
        } else {
          toast.warning(`⚠️ Partial sync: ${newLeads} new leads, ${errs} error${errs !== 1 ? "s" : ""}`);
        }
        handleRefresh();
      } else {
        toast.error("Sync failed. Please reconnect your integrations.");
      }
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error("Sync failed: " + (e.message || "Network error"));
    } finally {
      setSyncing(false);
    }
  };

  // Integration card data
  const integrationCards = [
    {
      id: "email",
      icon: Mail,
      accent: "#2563eb",
      name: "Email (IMAP)",
      description: "Sync leads from email accounts",
      status: integrations.length > 0 ? "connected" : "disconnected",
      connectedCount: integrations.length,
      lastSync: latestOf(integrations, "last_sync"),
      autoSync: integrations.some((i) => i.auto_sync),
      extraInfo: integrations.length > 0 ? [
        `${integrations.reduce((acc, i) => acc + (i.total_leads_captured || 0), 0)} total leads captured`,
        `${integrations.length} mailbox${integrations.length !== 1 ? "es" : ""} connected`,
      ] : undefined,
    },
    {
      id: "whatsapp",
      icon: MessageCircle,
      accent: "#22c55e",
      name: "WhatsApp",
      description: "WhatsApp Business API integration",
      status: waIntegrations.length > 0 ? "connected" : "disconnected",
      connectedCount: waIntegrations.length,
      lastSync: latestOf(waIntegrations, "last_sync_at"),
      autoSync: waIntegrations.some((i) => i.enabled),
      extraInfo: waIntegrations.length > 0 ? [
        `${waIntegrations.reduce((acc, i) => acc + (i.total_messages_received || 0), 0)} messages received`,
        waIntegrations[0]?.phone_number || "No phone",
      ] : undefined,
    },
    {
      id: "facebook",
      icon: Facebook,
      accent: "#1877f2",
      name: "Facebook Ads",
      description: "Capture leads from Facebook Lead Forms",
      status: fbIntegrations.length > 0 ? "connected" : "disconnected",
      connectedCount: fbIntegrations.length,
      lastSync: toDate(fbIntegrations[0]?.last_sync),
      autoSync: fbIntegrations.some((i) => i.enabled),
      extraInfo: fbIntegrations.length > 0 ? [
        `${fbIntegrations.reduce((acc, i) => acc + (i.forms_count || 0), 0)} active lead forms`,
        fbIntegrations[0]?.page_name || "Connected page",
      ] : undefined,
    },
    {
      id: "sheets",
      icon: Table2,
      accent: "#16a34a",
      name: "Google Sheets",
      description: "Import leads from spreadsheets",
      status: sheetIntegrations.length > 0 ? "connected" : "disconnected",
      connectedCount: sheetIntegrations.length,
      lastSync: latestOf(sheetIntegrations, "last_sync"),
      autoSync: sheetIntegrations.some((i) => i.auto_sync),
      extraInfo: sheetIntegrations.length > 0 ? [
        `${sheetIntegrations.reduce((acc, i) => acc + (i.total_leads_imported || 0), 0)} leads imported`,
        `Sync every ${sheetIntegrations[0]?.sync_interval || 5} min`,
      ] : undefined,
    },
    {
      id: "elevenlabs",
      icon: AudioLines,
      accent: "#0f172a",
      name: "SIYA AI Agent",
      description: "AI-powered voice conversations synced into the CRM",
      // "connected" only once ONE agent is verified — an API key alone leaves
      // sync disabled, so the card must not claim a working connection.
      status: elSettings?.sync_enabled ? "connected" : (elSettings?.has_api_key ? "error" : "disconnected"),
      connectedCount: elSettings?.sync_enabled ? 1 : 0,
      lastSync: toDate(elSettings?.last_poll_at),
      autoSync: elSettings?.poll_enabled !== false && !!elSettings?.sync_enabled,
      extraInfo: elSettings?.sync_enabled ? [
        `Agent: ${elSettings.elevenlabs_agent_name || elSettings.elevenlabs_agent_id}`,
        "Voice conversations + transcripts",
      ] : elSettings?.has_api_key ? [
        "Agent ID required — sync paused until one agent is verified",
      ] : undefined,
    },
    {
      id: "webhook",
      icon: Link2,
      accent: "#7c3aed",
      name: "Webhook",
      description: "Receive leads from any external system",
      status: webhookConfig ? "connected" : "disconnected",
      connectedCount: webhookConfig ? 1 : 0,
      lastSync: null,
      autoSync: webhookConfig?.status === "active",
      extraInfo: webhookConfig ? [
        `${(webhookConfig.total_leads_created || 0) + (webhookConfig.total_leads_updated || 0)} total leads`,
        `${webhookConfig.total_requests || 0} requests received`,
      ] : undefined,
    },
  ];

  const connectedCount = integrationCards.filter((c) => c.status === "connected").length;
  const totalCount = integrationCards.length;
  const leadsInPeriod = syncLogs.filter((l) => l.status === "success").reduce((acc, l) => acc + (l.leads_imported || 0), 0);
  const lastActivity = syncLogs.length > 0 ? syncLogs[0].created_at : null;
  const activeCard = integrationCards.find((c) => c.id === activeIntegration) || null;

  // Filter integrations
  const filteredCards = integrationCards.filter((card) => {
    const matchesSearch = card.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "connected" && card.status === "connected") ||
      (statusFilter === "disconnected" && card.status !== "connected");
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60">
        <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6 lg:p-8">
          <div className="flex items-end justify-between">
            <div><Skeleton className="h-7 w-44 rounded-lg" /><Skeleton className="mt-2 h-3.5 w-80 rounded" /></div>
            <Skeleton className="h-10 w-40 rounded-xl" />
          </div>
          <Skeleton className="h-20 w-full rounded-2xl" />
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-11 w-11 rounded-xl" />
                  <div className="flex-1"><Skeleton className="h-4 w-28 rounded" /><Skeleton className="mt-1.5 h-3 w-44 rounded" /></div>
                </div>
                <Skeleton className="h-16 w-full rounded-xl" />
                <Skeleton className="h-9 w-full rounded-lg" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Role-based access control
  if (currentUser && currentUser.role !== "admin" && currentUser.role !== "super_admin") {
    return (
      <div className="flex min-h-[420px] items-center justify-center p-4 md:p-6">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50">
            <Lock className="h-6 w-6 text-rose-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Access restricted</h2>
          <p className="mt-2 text-sm text-slate-500">Only Business Admins can configure integrations. Contact your administrator for access.</p>
          <span className="mt-4 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">Current role: {currentUser?.role}</span>
        </div>
      </div>
    );
  }

  const filterOptions = [
    { value: "all", label: "All", count: totalCount },
    { value: "connected", label: "Connected", count: connectedCount },
    { value: "disconnected", label: "Not connected", count: totalCount - connectedCount },
  ];

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="mx-auto max-w-7xl space-y-6 p-4 pb-24 md:p-6 md:pb-8 lg:p-8" data-testid="integrations-page">

        {/* Page header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Integrations</h1>
            <p className="mt-1 max-w-xl text-sm text-slate-500">
              Connect the tools your team already uses and let leads and conversations flow into Dnexus automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRefresh} title="Refresh status" className="h-10 w-10 rounded-xl border-slate-200 p-0 text-slate-600 hover:bg-white">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={handleSyncAll} disabled={syncing} className="h-10 rounded-xl bg-indigo-600 px-4 text-white hover:bg-indigo-700">
              {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
              Sync all sources
            </Button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <div className="px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Connected</p>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-2xl font-semibold tabular-nums text-slate-900">{connectedCount}</span>
              <span className="text-sm text-slate-400">of {totalCount}</span>
            </div>
            <div className="mt-2.5 flex gap-1">
              {integrationCards.map((c) => (
                <span key={c.id} title={`${c.name} — ${STATUS_META[c.status]?.label || ""}`}
                  className={`h-1.5 flex-1 rounded-full ${c.status === "connected" ? "bg-emerald-500" : c.status === "error" ? "bg-amber-400" : "bg-slate-200"}`} />
              ))}
            </div>
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Leads synced · {PERIOD_LABEL[dateFilter]}</p>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{leadsInPeriod}</div>
            <p className="mt-1 text-xs text-slate-500">across {syncLogs.filter((l) => l.status === "success").length} successful sync{syncLogs.filter((l) => l.status === "success").length === 1 ? "" : "s"}</p>
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Last activity</p>
            <div className="mt-1 text-2xl font-semibold text-slate-900">{lastActivity ? timeAgo(lastActivity) : "—"}</div>
            <p className="mt-1 text-xs text-slate-500">{lastActivity ? exactTime(lastActivity) : "No sync activity yet"}</p>
          </div>
        </div>

        {activeCard ? (
          /* ── Master / detail: compact list on the left, configuration on the right ── */
          <div className="grid items-start gap-6 lg:grid-cols-[264px_minmax(0,1fr)]">
            <aside className="lg:sticky lg:top-6">
              <button
                type="button"
                onClick={() => setActiveIntegration(null)}
                className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
                data-testid="integrations-back"
              >
                <ArrowLeft className="h-4 w-4" /> All integrations
              </button>
              <nav className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
                {integrationCards.map((c) => {
                  const active = c.id === activeIntegration;
                  const meta = STATUS_META[c.status] || STATUS_META.disconnected;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setActiveIntegration(c.id)}
                      className={`flex shrink-0 items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition lg:w-full ${
                        active ? "border-indigo-300 bg-indigo-50/70 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                      }`}
                    >
                      <IconTile icon={c.icon} accent={c.accent} size="sm" />
                      <span className="truncate font-medium">{c.name}</span>
                      <span className={`ml-auto h-2 w-2 shrink-0 rounded-full ${meta.dot}`} title={meta.label} />
                    </button>
                  );
                })}
              </nav>
            </aside>

            <section className="rounded-2xl border border-slate-200 bg-white" data-testid="integration-detail-panel">
              <header className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
                <div className="flex items-center gap-4">
                  <IconTile icon={activeCard.icon} accent={activeCard.accent} size="lg" />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{activeCard.name}</h2>
                    <p className="text-sm text-slate-500">{activeCard.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={activeCard.status} name={`${activeCard.name} detail`} />
                  <button type="button" onClick={() => setActiveIntegration(null)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Close">
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </header>
              <div className="p-5 sm:p-6">
                {activeIntegration === "email" && (
                  <EmailIntegration integrations={integrations} onRefresh={handleRefresh} />
                )}
                {activeIntegration === "whatsapp" && (
                  <div className="space-y-6">
                    <WhatsAppConnect currentUser={currentUser} businessId={businessId} onConnected={handleRefresh} />
                    <WhatsAppIntegration currentUser={currentUser} onRefresh={handleRefresh} hideConnect />
                  </div>
                )}
                {activeIntegration === "facebook" && (
                  <FacebookIntegration fbConfig={fbConfig} fbIntegrations={fbIntegrations} onRefresh={handleRefresh} />
                )}
                {activeIntegration === "sheets" && (
                  <GoogleSheetsIntegration currentUser={currentUser} sheetIntegrations={sheetIntegrations} onRefresh={handleRefresh} />
                )}
                {activeIntegration === "webhook" && (
                  <WebhookIntegration onRefresh={handleRefresh} />
                )}
                {activeIntegration === "elevenlabs" && (
                  <ElevenLabsIntegration onChanged={fetchElSettings} />
                )}
              </div>
            </section>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative sm:max-w-xs sm:flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search integrations…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 rounded-xl border-slate-200 bg-white pl-9"
                  data-testid="integrations-search"
                />
              </div>
              <div className="inline-flex self-start rounded-xl border border-slate-200 bg-white p-1">
                {filterOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setStatusFilter(o.value)}
                    data-testid={`integrations-filter-${o.value}`}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                      statusFilter === o.value ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {o.label}
                    <span className={`text-xs tabular-nums ${statusFilter === o.value ? "text-white/70" : "text-slate-400"}`}>{o.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Cards */}
            {filteredCards.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
                <Search className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-700">No integrations match</p>
                <p className="mt-1 text-xs text-slate-500">Try a different search or clear the filter.</p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filteredCards.map((card) => (
                  <IntegrationCard
                    key={card.id}
                    {...card}
                    isActive={activeIntegration === card.id}
                    onClick={() => setActiveIntegration(card.id === activeIntegration ? null : card.id)}
                    onConnect={() => setActiveIntegration(card.id)}
                    onConfigure={() => setActiveIntegration(card.id)}
                    onSyncNow={() => {
                      if (card.id === "sheets" && sheetIntegrations.length > 0) {
                        fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${sheetIntegrations[0].integration_id}/sync`, {
                          method: "POST",
                          credentials: "include",
                        }).then(() => {
                          toast.success("Sync completed!");
                          handleRefresh();
                        });
                      } else {
                        setActiveIntegration(card.id);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Sync Activity Logs */}
        <SyncActivityPanel
          logs={syncLogs}
          dateFilter={dateFilter}
          onDateFilterChange={(v) => {
            setDateFilter(v);
            // Re-fetch logs with new filter
            fetch(`${BACKEND_URL}/api/integrations/sync-logs?filter=${v}`, { credentials: "include" })
              .then((res) => res.json())
              .then((data) => setSyncLogs(data.logs || []))
              .catch(() => {});
          }}
        />

        {/* Mobile sticky sync button */}
        <div className="fixed bottom-4 left-4 right-4 z-40 md:hidden">
          <Button onClick={handleSyncAll} disabled={syncing} className="h-12 w-full rounded-xl bg-indigo-600 text-base text-white shadow-xl hover:bg-indigo-700">
            {syncing ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Zap className="mr-2 h-5 w-5" />}
            Sync all sources
          </Button>
        </div>
      </div>
    </div>
  );
}
